import React from 'react';
import { DotsLoader } from './SecureInputStage';
import { formatDeadline, formatMoney } from './hotelArtifact';
import type { HotelRateQuoteState } from './useHotelRateQuote';

interface HotelRateQuoteNoticeProps {
  state: HotelRateQuoteState;
  /** The previous quote lapsed before Book landed; this is the re-ask. */
  requoting?: boolean;
  assistantName: string;
  onRetry: () => void;
  onPickAnother: () => void;
}

/**
 * Live-quote status shown on the exact-rate approval sheet: the hotel is asked
 * for its current price and terms when the sheet opens, and Book stays
 * disabled until that answer arrives.
 */
const HotelRateQuoteNotice: React.FC<HotelRateQuoteNoticeProps> = ({
  state,
  requoting = false,
  assistantName,
  onRetry,
  onPickAnother,
}) => {
  if (state.status === 'idle' || state.status === 'quoting') {
    return (
      <div className='hav-quote hav-quote-pending' role='status'>
        <DotsLoader />
        <span>
          {requoting
            ? 'The hotel’s earlier price confirmation had expired, so nothing was booked. Confirming the current price and terms again…'
            : 'Confirming the current price and terms with the hotel…'}
        </span>
      </div>
    );
  }

  if (state.status === 'failed') {
    const copy =
      state.code === 'offer_expired'
        ? 'This rate is no longer available at the hotel. Pick another rate to continue.'
        : state.code === 'invalid_offer'
          ? `These results are out of date. Ask ${assistantName} to search again.`
          : 'The hotel did not confirm its price. Try again in a moment.';
    const canRetry =
      state.code === 'supplier_failed' || state.code === 'request_failed';
    return (
      <div className='hav-quote hav-quote-failed' role='alert'>
        <span>{copy}</span>
        <div className='hav-quote-actions'>
          {canRetry ? (
            <button type='button' className='hav-quote-btn' onClick={onRetry}>
              Try again
            </button>
          ) : null}
          <button
            type='button'
            className='hav-quote-btn'
            onClick={onPickAnother}
          >
            Pick another rate
          </button>
        </div>
      </div>
    );
  }

  const { quote } = state;
  if (state.status === 'expired') {
    return (
      <div className='hav-quote hav-quote-failed' role='alert'>
        <span>
          The hotel's price confirmation has expired. Confirm the current price
          to continue.
        </span>
        <div className='hav-quote-actions'>
          <button type='button' className='hav-quote-btn' onClick={onRetry}>
            Confirm price again
          </button>
        </div>
      </div>
    );
  }

  const priceChanged = quote.changed.price;
  const termsChanged = quote.changed.terms;
  return (
    <div
      className={`hav-quote ${priceChanged || termsChanged ? 'hav-quote-changed' : 'hav-quote-confirmed'}`}
      role='status'
    >
      {requoting ? (
        <span>
          The hotel’s earlier price confirmation had expired, so nothing was
          booked. Check the current figure and press Book again.
        </span>
      ) : null}{' '}
      {priceChanged ? (
        <span>
          The hotel's price has changed since these results were shown:{' '}
          {formatMoney(quote.shown.total, quote.shown.currency)} →{' '}
          <strong>{formatMoney(quote.total, quote.currency)}</strong>.
        </span>
      ) : (
        <span>
          Price confirmed by the hotel:{' '}
          <strong>{formatMoney(quote.total, quote.currency)}</strong>.
        </span>
      )}{' '}
      {termsChanged ? (
        <span>Cancellation terms have changed: {quote.cancellationTerms}.</span>
      ) : null}{' '}
      <span className='hav-quote-expiry'>
        Held until {formatDeadline(quote.expiresAt)}.
      </span>
    </div>
  );
};

export default HotelRateQuoteNotice;
